import { notEmpty } from '@vedalib/editor'
import React, { useCallback, useEffect, useRef } from 'react'

import type { INodeV2Props } from 'components/Editor/cource/layout/Node/types'
import type { NodeState } from 'services/Store/Project/types'

import SkeletonNodeV2 from '../SkeletonNodeV2'
import { applyStepStyles } from '../formControls/Animation/utils'
import * as s from './Controls.module.scss'

interface ITransformPreviewProps extends INodeV2Props {
  transform?: NodeState['transform']
  nodeRef: React.RefObject<HTMLDivElement>
}

const TransformPreview: React.FC<ITransformPreviewProps> = ({ transform, nodeRef, ...props }) => {
  const ref = useRef<HTMLDivElement>(null)
  const { node } = props

  const update = useCallback(() => {
    requestAnimationFrame(() => {
      const refs = [nodeRef.current].filter(notEmpty)
      const parentBox = nodeRef.current?.parentElement?.getBoundingClientRect() || {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }
      const boxes = refs.map((ref) => ref.getBoundingClientRect())
      const top = Math.min(...boxes.map((box) => box.top)) - parentBox.top
      const left = Math.min(...boxes.map((box) => box.left)) - parentBox.left
      const right = parentBox.right - Math.max(...boxes.map((box) => box.right))
      const bottom = parentBox.bottom - Math.max(...boxes.map((box) => box.bottom))
      const refStyles = { top, left, right, bottom }

      if (ref.current) {
        ref.current.style.top = `${refStyles.top}px`
        ref.current.style.bottom = `${refStyles.bottom}px`
        ref.current.style.left = `${refStyles.left}px`
        ref.current.style.right = `${refStyles.right}px`
      }
    })
  }, [nodeRef])

  useEffect(() => {
    const refs = [nodeRef.current].filter(notEmpty)
    update()
    const resizeObserver = new ResizeObserver(update)
    const observer = new MutationObserver(update)
    refs.forEach((ref) => resizeObserver.observe(ref))
    refs.forEach((ref) => observer.observe(ref, { attributes: true }))

    return () => {
      resizeObserver.disconnect()
      observer.disconnect()
    }
  }, [update, nodeRef])

  useEffect(() => {
    update()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={s.transform} ref={ref}>
      <SkeletonNodeV2 {...props} styles={applyStepStyles(node.transform, transform)} />
    </div>
  )
}

export default React.memo(TransformPreview)
